import { openDB } from 'idb';
import v1_actionDownloadMediaWhatsapp from 'src/actions/v1/whatsapp/v1_actionDownloadMediaWhatsapp';
import { soNumeros } from 'src/utils/functions';

// Função para abrir o banco de dados
const openImageDB = async () => {
  try {
    return await openDB('image-store', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('blobs')) {
          db.createObjectStore('blobs', { keyPath: 'id' });
        }
      },
    });
  } catch (error) {
    console.error('Erro ao abrir o banco de dados de imagens:', error);
    throw error;
  }
};

// Função para converter Base64 para Blob
export const base64ToBlob = (base64, mimeType) => {
  const byteCharacters = atob(base64.split(',')[1]); // Ignora a parte do cabeçalho da base64
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

// Função para gerar uma URL Blob
export const createBlobUrl = (blob) => {
  return URL.createObjectURL(blob);
};

// Função para recuperar o Blob do IndexedDB
export const getImageBlob = async (id) => {
  try {
    const db = await openImageDB();
    if (!db.objectStoreNames.contains('blobs')) {
      console.error('Object store "blobs" não encontrado');
      return null;
    }
    const data = await db.get('blobs', id);
    return data ? URL.createObjectURL(data.blob) : null;
  } catch (error) {
    console.error('Erro ao recuperar Blob:', error);
    return null;
  }
};

export async function initCacheFileSave(id, base64, mimeType) {
  try {
    const response = await getImageBlob(id);
    if (response) return;
    
    const blob = base64ToBlob(base64, mimeType);
    await storeImageBlob(id, blob);
  } catch (error) {
    console.error('Erro ao inicializar cache de arquivo:', error);
  }
}

// Função para armazenar o Blob no IndexedDB
export const storeImageBlob = async (id, blob) => {
  try {
    const db = await openImageDB();
    if (!db.objectStoreNames.contains('blobs')) {
      console.error('Object store "blobs" não encontrado');
      return;
    }
    await db.put('blobs', { id, blob });
  } catch (error) {
    console.error('Erro ao armazenar Blob:', error);
  }
};

// Remove um blob específico do cache (áudio/mídia de uma mensagem que falhou).
// Sem isso, apagar o balão de erro deixava o áudio órfão no IndexedDB para sempre.
export const removeImageBlob = async (id) => {
  try {
    const db = await openImageDB();
    if (!db.objectStoreNames.contains('blobs')) return;
    await db.delete('blobs', id);
  } catch (error) {
    console.error('Erro ao remover Blob:', error);
  }
};

export const deleteAllCacheFiles = async () => {
  try {
    const db = await openDB('image-store', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('blobs')) {
          db.createObjectStore('blobs', { keyPath: 'id' });
        }
      }
    });
    
    if (!db.objectStoreNames.contains('blobs')) {
      console.error('Object store "blobs" não encontrado');
      return;
    }
    
    const tx = db.transaction('blobs', 'readwrite');
    const store = tx.objectStore('blobs');
    await store.clear();
    await tx.done;
    console.log('Todas as imagens foram deletadas do IndexedDB.');
  } catch (error) {
    console.error('Erro ao deletar as imagens do IndexedDB:', error);
  }
};

export const downloadMediaAndSaveCache = async (message, chat) => {
  try {
    let querys = `?sessao=${chat.sessao}&msgId=${message.id}&canal=whatsapp`;
    const response = await v1_actionDownloadMediaWhatsapp(querys);

    if (response.status === 200) {
      let number = soNumeros(message.chatId);
      if (chat.numeroCliente === number) {
        await initCacheFileSave(message.id, `data:image/jpeg;base64, ${response.data.data.base64}`, message.mimetype);
      }
    }
  } catch (error) {
    console.error('Erro ao baixar e salvar mídia:', error);
  }
};

export async function saveMessageToIDB(dbName, messageData) {
  try {
    const db = await openDB(dbName, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('failedMessages')) {
          db.createObjectStore('failedMessages', { keyPath: 'id', autoIncrement: true });
        }
      }
    });
    
    if (!db.objectStoreNames.contains('failedMessages')) {
      console.error('Object store "failedMessages" não encontrado');
      return;
    }
    
    await db.add('failedMessages', messageData);
  } catch (error) {
    console.error('Erro ao salvar mensagem no IDB:', error);
  }
}

export async function removeMessageFromIDB(dbName, key) {
  try {
    const db = await openDB(dbName, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('failedMessages')) {
          db.createObjectStore('failedMessages', { keyPath: 'id', autoIncrement: true });
        }
      }
    });
    
    if (!db.objectStoreNames.contains('failedMessages')) {
      console.error('Object store "failedMessages" não encontrado');
      return;
    }
    
    await db.delete('failedMessages', key);
  } catch (error) {
    console.error('Erro ao remover mensagem do IDB:', error);
  }
}

export async function getMessagesBySubstring(substring) {
  try {
    const db = await openDB('error_msg_send', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('failedMessages')) {
          const store = db.createObjectStore('failedMessages', { keyPath: 'id', autoIncrement: true });
          store.createIndex('id', 'id', { unique: true });
        }
      }
    });
    
    if (!db.objectStoreNames.contains('failedMessages')) {
      console.error('Object store "failedMessages" não encontrado');
      return [];
    }

    const tx = db.transaction('failedMessages', 'readonly');
    const store = tx.objectStore('failedMessages');
    const allMessages = await store.getAll();
    await tx.done;

    const needle = String(substring).replace(/_$/, '');
    if (!needle) return [];
    return allMessages.filter((msg) => {
      if (msg?.chat_id && String(msg.chat_id) === needle) return true;
      const id = msg?.id != null ? String(msg.id) : '';
      // Prefixo com `_` evita colisão entre ObjectIds parecidos.
      return Boolean(id && id.startsWith(`${needle}_`));
    });
  } catch (error) {
    console.error('Erro ao buscar mensagens por substring:', error);
    return [];
  }
}